import type { BetterDocConsultationQuestionId } from "@/modules/patients/detail/better-doc/de/lib/better-doc-consultation-questionnaire-definition"
import { getVisibleQuestions } from "@/modules/patients/detail/better-doc/de/lib/better-doc-consultation-questionnaire-visibility"
import type { DeBetterDocSearchFormType } from "@/modules/patients/detail/better-doc/de/schema/de-better-doc-search-schema"
import type { De_requestBetterDocRecommendationInput } from "@/modules/patients/detail/better-doc/de/services/__generated__/useRequestBetterDocRecommendationMutation.graphql"
import type { BetterDocSpecialty } from "@/modules/patients/detail/better-doc/lib/better-doc-sub-specialties"
import type { BetterDocAddress, DeBetterDocInsurance } from "@/modules/patients/detail/better-doc/types"
import type { PointOfCareType } from "@/types/__generated__/union-types"

type CreateRequestBetterDocRecommendationInputParameters = {
  progressNoteId: string
  effectivePointOfCare: PointOfCareType
  pointOfCareSpecialtyId: string | null
  answers: DeBetterDocSearchFormType
  specialtyName: BetterDocSpecialty | undefined
  preferredMedicActivityId: number | null
  address: BetterDocAddress
  phoneNumber: string
  insurance: DeBetterDocInsurance
}

const toYesNoAnswer = (answer: string | null): boolean | null => {
  if (answer === "yes") return true
  if (answer === "no") return false
  return null
}

export const createRequestBetterDocRecommendationInput = ({
  progressNoteId,
  effectivePointOfCare,
  pointOfCareSpecialtyId,
  answers,
  specialtyName,
  preferredMedicActivityId,
  address,
  phoneNumber,
  insurance,
}: CreateRequestBetterDocRecommendationInputParameters): De_requestBetterDocRecommendationInput => {
  const visibleQuestionIds = new Set(getVisibleQuestions(answers, specialtyName).map((question) => question.id))

  const getVisibleAnswer = (questionId: BetterDocConsultationQuestionId): string | null => {
    if (!visibleQuestionIds.has(questionId)) return null
    const trimmedAnswer = answers[questionId]?.trim()
    return trimmedAnswer || null
  }

  const getVisibleSelectAnswer = <TAnswer extends string>(
    questionId: BetterDocConsultationQuestionId,
    answer: TAnswer | undefined,
  ): TAnswer | null => (visibleQuestionIds.has(questionId) ? answer || null : null)

  return {
    progressNoteId,
    effectivePointOfCare,
    pointOfCareSpecialtyId,
    answers: {
      reasonForConsultation: answers.reasonForConsultations,
      preventiveCareScreening: getVisibleSelectAnswer("preventiveCareScreening", answers.preventiveCareScreening),
      childAndAdolescentCheckUpDetails: getVisibleSelectAnswer(
        "childAndAdolescentCheckUpDetails",
        answers.childAndAdolescentCheckUpDetails,
      ),
      specificExamination: getVisibleSelectAnswer("specificExamination", answers.specificExamination),
      bodyPartToExamine: getVisibleAnswer("bodyPartToExamine"),
      allergiesToTest: getVisibleAnswer("allergiesToTest"),
      bodyRegion: getVisibleSelectAnswer("bodyRegion", answers.bodyRegion),
      previousDoctor: getVisibleSelectAnswer("previousDoctor", answers.previousDoctor),
      excludingDiagnosis: getVisibleSelectAnswer("excludingDiagnosis", answers.excludingDiagnosis),
      symptomLocation: getVisibleSelectAnswer("symptomLocation", answers.symptomLocation),
      magneticResonanceImagingDone: toYesNoAnswer(getVisibleAnswer("magneticResonanceImagingDone")),
      symptomsLongerThanOneMonth: toYesNoAnswer(getVisibleAnswer("symptomsLongerThanOneMonth")),
    },
    subSpecialty: getVisibleAnswer("subSpecialty"),
    preferredMedicActivityId,
    insurance,
    address,
    phoneNumber,
  }
}
